import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { 
  fetchProgramById, 
  fetchSessionsByProgram, 
  operationsBase, 
  TABLES 
} from '@/lib/airtable';
import { 
  getCachedData, 
  setCachedData, 
  getProgramDetailsCacheKey, 
  getProgramSessionsCacheKey, 
  clearCachedData 
} from '@/lib/cache';
import { 
  ArrowLeft,
  Calendar,
  FileText,
  Users,
  Clock,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  History,
  Settings
} from 'lucide-react';

/**
 * ProgramSelector - Program-level materials management interface
 * 
 * Shows all sessions within a program and their material status.
 * Allows navigation to session-specific material management.
 * 
 * Features:
 * - Session overview with material version info
 * - Bulk operations across all sessions
 * - Version history at program level
 * - Impact preview for changes
 */
const ProgramSelector = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProgramData = async () => {
      try {
        setLoading(true);
        
        // Try to get cached data first
        const programCacheKey = getProgramDetailsCacheKey(programId);
        const sessionsCacheKey = getProgramSessionsCacheKey(programId);
        
        const cachedProgram = getCachedData(programCacheKey);
        const cachedSessions = getCachedData(sessionsCacheKey);
        
        if (cachedProgram && cachedSessions) {
          console.log('Using cached program and sessions data');
          setProgram(cachedProgram);
          setSessions(cachedSessions);
          setLoading(false);
          return;
        }
        
        // Load real program data from Airtable
        const [programData, sessionsData] = await Promise.all([
          fetchProgramById(programId),
          fetchSessionsByProgram(programId)
        ]);
        
        // Get additional program statistics
        const soResponse = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
          .select({
            filterByFormula: `FIND("${programId}", {Program})`,
            fields: ['SO', 'Delivery Start', 'Delivery End']
          })
          .all();
        
        const activeSOs = soResponse.filter(so => {
          const endDate = so.get('Delivery End');
          if (!endDate) return true; // No end date means ongoing
          return new Date(endDate) >= new Date(); // Active if end date is in the future
        }).length;
        
        // Calculate total materials count from all sessions
        const totalMaterials = sessionsData.reduce((sum, session) => 
          sum + (session.materialCount || 0), 0
        );
        
        // Format program data
        const enrichedProgram = {
          id: programData.id,
          name: programData.name,
          code: programData.code || (programData.name ? programData.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 3) : 'PRG'),
          description: programData.description || `Program covering ${sessionsData.length} sessions with comprehensive materials and resources.`,
          activeSOs: activeSOs,
          totalMaterials: totalMaterials,
          lastUpdated: programData.lastModified || new Date().toISOString(),
          currentVersion: 'v1.0.0' // Default version - would need materials versioning API
        };
        
        // Format sessions data with placeholder materials information
        // TODO: Replace with real materials data once materials management tables are set up
        const enrichedSessions = await Promise.all(
          sessionsData.map(async (session, index) => {
            const sessionDuration = 90; // Default duration - could be from session data if available
            
            // Use placeholder materials data until materials management is fully implemented
            const materialCount = 3 + Math.floor(Math.random() * 5); // 3-7 placeholder materials
            const materialsSize = `${(Math.random() * 15 + 5).toFixed(1)} MB`; // 5-20MB placeholder
            const currentVersion = 'v1.0.0'; // Default version
            const hasNewVersion = Math.random() > 0.7; // 30% chance of showing "new version" for demo
            
            // Determine session status with placeholder logic
            // TODO: Replace with real status logic once materials management is implemented
            let status = 'current';
            if (hasNewVersion) {
              status = 'updated';
            } else if (materialCount <= 3) {
              status = 'needs_update';
            }
            
            return {
              id: session.id,
              name: session.name,
              sessionNumber: session.sessionNo || (index + 1),
              duration: sessionDuration,
              materialCount: materialCount,
              currentVersion: currentVersion,
              lastUpdated: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(), // Random date within last 30 days
              hasNewVersion: hasNewVersion,
              materialsSize: materialsSize,
              activeSOs: activeSOs, // Use program-level active SO count
              status: status
            };
          })
        );
        
        // Cache the data
        setCachedData(programCacheKey, enrichedProgram);
        setCachedData(sessionsCacheKey, enrichedSessions);
        
        setProgram(enrichedProgram);
        setSessions(enrichedSessions);
        
      } catch (err) {
        console.error('Failed to load program data:', err);
        if (err.message?.includes('NOT_FOUND')) {
          setError('Program not found');
        } else {
          setError('Failed to load program information');
        }
      } finally {
        setLoading(false);
      }
    };

    loadProgramData();
  }, [programId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'current':
        return <Badge variant="default" className="bg-green-100 text-green-800">Current</Badge>;
      case 'updated':
        return <Badge variant="default" className="bg-blue-100 text-blue-800">Updated</Badge>;
      case 'older_version':
        return <Badge variant="secondary">Older Version</Badge>;
      case 'needs_update':
        return <Badge variant="destructive" className="bg-amber-100 text-amber-800">Needs Update</Badge>;
      default:
        return null;
    }
  };

  const handleRefreshData = () => {
    // Clear cached data for this program
    clearCachedData(getProgramDetailsCacheKey(programId));
    clearCachedData(getProgramSessionsCacheKey(programId));
    
    // Reload the data
    setLoading(true);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="space-y-10">
        {/* Status Notice Skeleton */}
        <div className="bg-gray-50 border rounded-lg p-4">
          <Skeleton className="h-4 w-full max-w-3xl" />
        </div>

        {/* Header Skeleton */}
        <div className="space-y-6">
          <Skeleton className="h-6 w-32" />
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Skeleton className="h-10 w-80" />
              <Skeleton className="h-8 w-16" />
            </div>
            <Skeleton className="h-5 w-full max-w-4xl" />
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Skeleton className="h-5 w-5 rounded-full" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Sessions List Skeleton */}
        <Card>
          <CardHeader className="pb-6">
            <div className="flex items-center space-x-3">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-6 w-40" />
            </div>
            <Skeleton className="h-4 w-80 mt-2" />
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="p-6 border rounded-xl">
                  <div className="flex flex-col lg:flex-row lg:justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                        <Skeleton className="h-6 w-64" />
                        <div className="flex gap-2">
                          <Skeleton className="h-6 w-16 rounded-full" />
                          <Skeleton className="h-6 w-20 rounded-full" />
                        </div>
                      </div>
                      <Skeleton className="h-4 w-48" />
                    </div>
                    <Skeleton className="h-8 w-32" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, j) => (
                      <div key={j} className="bg-gray-50 rounded-lg p-3">
                        <Skeleton className="h-4 w-full" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/admin/materials')}
          className="flex items-center space-x-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Materials</span>
        </Button>
        
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-600 font-medium">Failed to load program</p>
            <p className="text-gray-500 text-sm mt-1">{error || 'Program not found'}</p>
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline" 
              className="mt-4"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Materials Management Status Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-amber-800">Materials Management In Development</h3>
            <p className="text-sm text-amber-700 mt-1">
              This interface shows program and session information. Materials management features will be available once the materials database tables are set up. 
              Current data shown is placeholder information for demonstration purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Header with breadcrumb */}
      <div className="space-y-6">
        {/* Breadcrumb */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/admin/materials')}
          className="flex items-center space-x-2 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Materials</span>
        </Button>
        
        {/* Program Header */}
        <div className="space-y-6">
          {/* Action Buttons - Above Title */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:justify-start">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center space-x-2 whitespace-nowrap"
              onClick={handleRefreshData}
              title="Clear cache and refresh program data"
            >
              <Clock className="h-4 w-4" />
              <span>Refresh Data</span>
            </Button>
            <Button 
              variant="outline"
              size="sm" 
              className="flex items-center space-x-2 whitespace-nowrap"
              disabled
              title="Materials management features coming soon"
            >
              <FileText className="h-4 w-4" />
              <span>Manage Program Materials</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center space-x-2 whitespace-nowrap" 
              disabled 
              title="Version history coming soon"
            >
              <History className="h-4 w-4" />
              <span>Version History</span>
            </Button>
          </div>

          {/* Program Title and Description - Full Width */}
          <div className="space-y-4 w-full">
            <div className="flex flex-col sm:flex-row sm:items-start lg:items-center gap-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-gray-900 leading-[1.1] sm:leading-tight">
                {program.name}
              </h1>
              <Badge variant="secondary" className="text-sm sm:text-base lg:text-lg px-3 py-1.5 lg:px-4 lg:py-2 w-fit shrink-0 mt-1 sm:mt-0">
                {program.code}
              </Badge>
            </div>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed w-full">
              {program.description}
            </p>
          </div>
        </div>
      </div>

      {/* Program Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 text-gray-500 text-sm mb-3">
              <Calendar className="h-5 w-5 text-blue-500" />
              <span className="font-medium">Sessions</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{sessions.length}</div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 text-gray-500 text-sm mb-3">
              <FileText className="h-5 w-5 text-green-500" />
              <span className="font-medium">Total Materials</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{program.totalMaterials}</div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 text-gray-500 text-sm mb-3">
              <Users className="h-5 w-5 text-purple-500" />
              <span className="font-medium">Active SOs</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{program.activeSOs}</div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 text-gray-500 text-sm mb-3">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              <span className="font-medium">Current Version</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{program.currentVersion}</div>
          </CardContent>
        </Card>
      </div>

      {/* Sessions List */}
      <Card>
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center space-x-3 text-xl">
            <Calendar className="h-6 w-6 text-blue-500" />
            <span>Program Sessions</span>
          </CardTitle>
          <CardDescription className="text-base">
            Manage materials for each session in this program
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {sessions.map((session) => (
              <div 
                key={session.id}
                className="p-6 border rounded-xl hover:bg-gray-50 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Session {session.sessionNumber}: {session.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(session.status)}
                        {session.hasNewVersion && (
                          <Badge variant="default" className="bg-blue-100 text-blue-800">
                            New Version Available
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4">
                      {session.duration} minutes • Version {session.currentVersion}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex items-center space-x-2 whitespace-nowrap"
                    disabled
                    title="Session materials management coming soon"
                  >
                    <span>Manage (Coming Soon)</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3">
                    <FileText className="h-4 w-4 text-green-500" />
                    <span className="font-medium">{session.materialCount} files</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3">
                    <Users className="h-4 w-4 text-purple-500" />
                    <span className="font-medium">{session.activeSOs} active SOs</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3">
                    <div className="h-4 w-4 rounded-full bg-blue-500 flex items-center justify-center">
                      <div className="h-2 w-2 rounded-full bg-white"></div>
                    </div>
                    <span className="font-medium">{session.materialsSize}</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="font-medium">Updated {formatDate(session.lastUpdated)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProgramSelector;